import { useEffect, useMemo, useRef, useState } from 'react';
import './ImageSlider.css';

function normalizeImages(images) {
  if (!Array.isArray(images)) {
    return [];
  }

  return images
    .map((item, index) => {
      if (!item) {
        return null;
      }

      if (typeof item === 'string') {
        return {
          id: `slide-${index}`,
          src: item,
          alt: `Slide ${index + 1}`,
        };
      }

      if (typeof item === 'object') {
        return {
          id: item.id ?? `slide-${index}`,
          src: item.src,
          alt: item.alt ?? `Slide ${index + 1}`,
        };
      }

      return null;
    })
    .filter(Boolean);
}

const DEFAULT_INTERVAL = 5000;

function ImageSlider({ images, autoPlayInterval = DEFAULT_INTERVAL, className = '', ariaLabel = '首頁輪播' }) {
  const slides = useMemo(() => normalizeImages(images), [images]);
  const [activeIndex, setActiveIndex] = useState(0);
  const timerRef = useRef();

  useEffect(() => {
    setActiveIndex(0);
  }, [slides.length]);

  useEffect(() => {
    if (!slides.length) {
      return undefined;
    }

    const runAutoPlay = () => {
      clearInterval(timerRef.current);
      timerRef.current = setInterval(() => {
        setActiveIndex((prev) => (prev + 1) % slides.length);
      }, Math.max(autoPlayInterval, 1000));
    };

    runAutoPlay();

    return () => {
      clearInterval(timerRef.current);
    };
  }, [autoPlayInterval, slides.length]);

  const handleSelect = (index) => {
    if (!slides.length) {
      return;
    }

    setActiveIndex(index);
    clearInterval(timerRef.current);
    timerRef.current = setInterval(() => {
      setActiveIndex((prev) => (prev + 1) % slides.length);
    }, Math.max(autoPlayInterval, 1000));
  };

  const handlePrev = () => {
    if (!slides.length) {
      return;
    }

    const nextIndex = (activeIndex - 1 + slides.length) % slides.length;
    handleSelect(nextIndex);
  };

  const handleNext = () => {
    if (!slides.length) {
      return;
    }

    const nextIndex = (activeIndex + 1) % slides.length;
    handleSelect(nextIndex);
  };

  if (!slides.length) {
    return null;
  }

  return (
    <div className={`image-slider ${className}`.trim()} role="region" aria-label={ariaLabel}>
      <button
        type="button"
        className="image-slider__nav image-slider__nav--prev"
        onClick={handlePrev}
        aria-label="上一張"
      >
        <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" aria-hidden="true">
          <path d="M15 19l-7-7 7-7" fill="none" stroke="currentColor" strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" />
        </svg>
      </button>

      <button
        type="button"
        className="image-slider__nav image-slider__nav--next"
        onClick={handleNext}
        aria-label="下一張"
      >
        <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" aria-hidden="true">
          <path d="M9 5l7 7-7 7" fill="none" stroke="currentColor" strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" />
        </svg>
      </button>

      <div className="image-slider__slides">
        {slides.map((slide, index) => (
          <div
            key={slide.id ?? slide.src}
            className={`image-slider__slide${index === activeIndex ? ' image-slider__slide--active' : ''}`}
            aria-hidden={index !== activeIndex}
          >
            <img src={slide.src} alt={slide.alt} />
          </div>
        ))}
      </div>

      <div className="image-slider__dots">
        {slides.map((slide, index) => (
          <button
            key={slide.id ?? slide.src}
            type="button"
            className={`image-slider__dot${index === activeIndex ? ' image-slider__dot--active' : ''}`}
            onClick={() => handleSelect(index)}
            aria-label={`切換到第 ${index + 1} 張`}
          />
        ))}
      </div>
    </div>
  );
}

export default ImageSlider;
